import Dialog from "@components/Dialog"
import FileUploader from "@components/FileUploader"
import ScenarioFileTableExample from "@features/builder/UpdateMetadata/ScenarioFileTableExample"
import { MetadataFormValues } from "@features/builder/UpdateMetadata/validation"
import FileContentPreviewTable from "@features/upload/FileContentPreviewTable"
import { Button, Grid, Typography } from "@mui/material"
import { getTableRowsFromCsvContent, readFile } from "@utils/file"
import { useState } from "react"
import { useFormContext } from "react-hook-form"

const ALLOWED_FILE_EXTENSIONS = [".csv"]
const REQUIRED_HEADERS = ["Date", "Mnem", "Scenario", "Value"]

type ScenarioFileUploadDialogProps = {
  onClose: () => void
}

const ScenarioFileUploadDialog = (props: ScenarioFileUploadDialogProps) => {
  const [file, setFile] = useState<File | null>(null)
  const form = useFormContext<MetadataFormValues>()

  const handleUpload = () => {
    if (!file) return

    form.clearErrors("scenario_source")
    form.setValue("scenario_source", file)
    props.onClose()
  }

  const handleValidateFile = async (file: File) => {
    const fileExtension = `.${file.name.split(".").pop()?.toLowerCase() || ""}`
    if (!ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
      return [`File type not allowed. Allowed types: ${ALLOWED_FILE_EXTENSIONS.join(", ")}`]
    }

    const fileContent = await readFile(file)
    const [firstDataRow] = getTableRowsFromCsvContent(fileContent, 1)
    if (!firstDataRow) return ["File is empty"]

    const missingHeaders = Object.keys(firstDataRow).reduce<string[]>(
      (acc, header) => {
        if (!REQUIRED_HEADERS.includes(header)) return acc

        return acc.filter((item) => item !== header)
      },
      [...REQUIRED_HEADERS],
    )

    if (missingHeaders.length > 0) {
      return [`Missing required headers: ${missingHeaders.join(", ")}`]
    }

    return []
  }

  return (
    <Dialog
      title="Select scenario source file"
      open
      maxWidth={false}
      onClose={props.onClose}
      actionsComponent={
        <>
          <Button onClick={props.onClose} variant="contained">
            Cancel
          </Button>
          <Button onClick={handleUpload} color="error" variant="contained" disabled={!file}>
            Upload
          </Button>
        </>
      }
    >
      <Grid container direction="column" gap={5}>
        <Grid container item direction="column" gap={3}>
          <Grid item>
            <Typography variant="h5" component="div">
              What is a scenario source file?
            </Typography>
            <Typography variant="body2">
              Scenario source file is a file that allows you to define values of individual nodes
              for every sampling step and for each scenario that you want to define for this graph.
              <br />
              <br />
              For scenario type nodes: In scenario type nodes, the mnemonic used in 'Scenario
              Mnemonic' field is defined within this file. Imagine you want to test your graph in
              two cases: optimistic and pessimistic. Depending on scenario you want to test your
              graph against, it will use the simple forecast values defined for that scenario, under
              this mnemonic. The scenario data must define all time steps between start date and end
              date of the graph, inclusive, for that mnemonic and scenario.
              <br />
              <br />
              For forecast type nodes: In this file you may also define forecast data (defined in
              each Forecast node's Forecast Mnemonic field). These are usually simple forecasts
              computed or obtained off-system, and do not have the Scenario column defined in the
              data (leave it blank).
              <br />
              <br />
              For all node types: In this file you may also define historic data (defined in each
              node's Historic Mnemonic field), which will be displayed in time-series charts of the
              results. In this manner we will be able to see actual historic data alongside the
              forecast data from sampling
            </Typography>
          </Grid>

          <Grid item>
            <Typography variant="h5" component="div">
              How can I create a scenario file?
            </Typography>
            <Typography variant="body2">
              First of all, your file needs to be a comma-separated <code>.csv</code> file (you can
              save any Excel file as a file with extension of <code>.csv</code>). <br />
              You are also required to follow a specific but very simple structure that you can see
              below. <br />
              <code>Scenario</code> value is optional, if not present it will be treated as forecast
              / historical data.
            </Typography>
          </Grid>
          <Grid item>
            <ScenarioFileTableExample />
            <Typography>
              You can find full example file{" "}
              <a href="src/assets/scenario-example.csv" download>
                here
              </a>
              .
            </Typography>
          </Grid>
        </Grid>

        <Grid container item direction="column" gap={3}>
          <Grid item>
            <FileUploader
              allowedFormats={ALLOWED_FILE_EXTENSIONS}
              onUpload={setFile}
              validate={handleValidateFile}
            />
          </Grid>
          {file && <FileContentPreviewTable file={file} maxRows={1000} />}
        </Grid>
      </Grid>
    </Dialog>
  )
}

export default ScenarioFileUploadDialog
